/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

/**
 * Timeline Commits API Client
 *
 * Handles all API calls to the Python FastAPI backend for Timeline Commits.
 * Automatically injects Supabase JWT tokens for authentication.
 */

import { supabase } from '@/lib/supabase'

// =============================================
// TYPES (matching backend schemas)
// =============================================

export interface CommitCreateRequest {
  sequence_id: string
  message: string
  otio_timeline: string
  author_name?: string
  metadata?: Record<string, any>
}

export interface CommitResponse {
  id: string
  sequence_id: string
  user_id: string
  message: string
  author_name: string | null
  commit_number: number
  parent_commit_id: string | null
  otio_file_url: string  // Supabase Storage URL for OTIO timeline file
  commit_metadata: Record<string, any>
  created_at: string
}

export interface CommitListResponse {
  commits: CommitResponse[]
  total: number
  sequence_id: string
}

export interface ClipChange {
  change_type: 'added' | 'removed' | 'trimmed' | 'moved' | 'metadata'
  clip_name: string
  nle_unique_id?: string
  old_source_range?: Record<string, any>
  new_source_range?: Record<string, any>
  old_timeline_range?: Record<string, any>
  new_timeline_range?: Record<string, any>
  metadata_changes?: Record<string, any>
}

export interface VfxShotChange {
  shot_name: string
  change_type: 'frame_range_changed'
  old_start: number
  old_end: number
  old_duration: number
  new_start: number
  new_end: number
  new_duration: number
  frame_difference: number
  nle_unique_id?: string
}

export interface CommitDiffResponse {
  from_commit: CommitResponse
  to_commit: CommitResponse
  changes: ClipChange[]
  vfx_changes?: VfxShotChange[]
  summary: {
    added: number
    removed: number
    trimmed: number
    moved: number
    metadata: number
    vfx_frame_changes?: number
  }
}

// =============================================
// TIMELINE COMPARISON TYPES (AIO-46)
// =============================================

export interface ComparisonCreateRequest {
  compare_to_commit_id: string
  detect_vfx_changes?: boolean
  notify_team?: boolean
}

// =============================================
// ACTIVITY DETAILS TYPES
// =============================================

export interface VfxWarning {
  marker_name: string
  warning_type: 'needs_transcode' | 'repositioned' | 'removed'
  reason: string
}

export interface ChangedVfxShot {
  marker_name: string
  old_visible_range?: number[][]
  new_visible_range?: number[][]
  old_duration?: number
  new_duration?: number
  duration_delta?: number
  old_frame_range?: number[]
  new_frame_range?: number[]
  frame_shift?: number
  duration_unchanged?: boolean
  affected_clips: string[]
  track?: string
}

export interface NewVfxShot {
  marker_name: string
  frame_range: number[]
  duration: number
  affected_clips: string[]
  track: string
}

export interface OmitVfxShot {
  marker_name: string
  old_frame_range: number[]
  old_duration: number
  track: string
}

export interface ChangedClip {
  clip_name: string
  track: string
  nle_unique_id?: string
  tape_name?: string
  clip_id?: string
  old_visible_range?: number[][]
  new_visible_range?: number[][]
  old_position?: number[]
  new_position?: number[]
  frame_shift?: number
  old_duration?: number
  new_duration?: number
  duration_change?: number
  change_type: 'occlusion' | 'repositioned' | 'trimmed' | 'extended'
  affects_vfx: string[]
  is_completely_hidden?: boolean
}

export interface NewClip {
  clip_name: string
  track: string
  nle_unique_id?: string
  tape_name?: string
  clip_id?: string
  frame_range: number[]
  duration: number
  impact: 'occludes_vfx' | 'none'
  affects_vfx: string[]
  is_completely_hidden?: boolean
}

export interface RemovedClip {
  clip_name: string
  track: string
  nle_unique_id?: string
  tape_name?: string
  clip_id?: string
  old_frame_range: number[]
  old_duration: number
  affected_vfx: string[]
  is_completely_hidden?: boolean
}

export interface MovedClip {
  clip_name: string
  old_track: string
  new_track: string
  nle_unique_id?: string
  tape_name?: string
  clip_id?: string
  frame_range: number[]
  duration: number
  position_changed: boolean
  old_position?: number[]
  new_position?: number[]
  affects_vfx: string[]
}

export interface ActivityDetailsResponse {
  vfx_warnings: VfxWarning[]
  changed_vfx_shots: ChangedVfxShot[]
  new_vfx_shots: NewVfxShot[]
  omit_vfx_shots: OmitVfxShot[]
  changed_clips: ChangedClip[]
  new_clips: NewClip[]
  removed_clips: RemovedClip[]
  moved_clips: MovedClip[]
}

export interface ComparisonResponse {
  id: string
  project_id: string
  from_commit_id: string
  to_commit_id: string
  changes: ClipChange[]
  vfx_changes: VfxShotChange[]
  summary: {
    total_changes: number
    omitted: number
    added: number
    modified: number
    total_clips_from: number
    total_clips_to: number
    trimmed?: number
    moved?: number
    metadata?: number
    vfx_changes?: number
  }
  activity_details?: ActivityDetailsResponse
  created_by: string
  created_at: string
  updated_at: string
}

export interface ComparisonListResponse {
  comparisons: ComparisonResponse[]
  total: number
  project_id: string
}

// =============================================
// JOB TYPES (for async processing)
// =============================================

export interface CommitJobResponse {
  job_id: string
  sequence_id: string
  message: string
  status: string
}

export interface JobResponse {
  id: string
  job_type: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  progress_percent: number
  status_message: string | null
  current_step: string | null
  total_steps: number | null
  error_message: string | null
  retry_count: number
  max_retries: number
  result_data: any | null
  result_storage_path: string | null
  created_at: string
  started_at: string | null
  completed_at: string | null
}

// =============================================
// API CLIENT CONFIGURATION
// =============================================

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

/**
 * Get the current Supabase JWT token for authentication
 */
async function getAuthToken(): Promise<string> {
  const { data: { session }, error } = await supabase.auth.getSession()

  if (error) {
    console.error('Auth error:', error)
    throw new Error('Authentication error. Please try logging in again.')
  }

  if (!session) {
    throw new Error('Not authenticated. Please log in.')
  }

  return session.access_token
}

/**
 * Make an authenticated API request to the backend
 * OPTIMIZED: Added timeout handling to prevent hanging
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken()

  const url = `${BACKEND_URL}${endpoint}`

  // Create an AbortController for timeout
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
      console.error(`[API] Error ${response.status}:`, errorData)

      // Add helpful context for 401 errors
      if (response.status === 401) {
        throw new Error('Authentication failed. Please check:\n1. Backend is running\n2. SUPABASE_JWT_SECRET is configured correctly in backend/.env\n3. You are logged in')
      }

      throw new Error(errorData.detail || `API error: ${response.statusText}`)
    }

    // Handle responses with no content (e.g., 204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T
    }

    return await response.json()
  } catch (error: any) {
    clearTimeout(timeoutId)

    // Handle timeout errors
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. The backend may be slow or unreachable.')
    }

    throw error
  }
}

// =============================================
// API METHODS
// =============================================

/**
 * Create a new timeline commit (async with job tracking)
 *
 * @param data Commit data including sequence_id, message, and OTIO timeline JSON
 * @returns Job response with job_id for polling
 */
export async function createCommit(
  data: CommitCreateRequest
): Promise<CommitJobResponse> {
  return apiRequest<CommitJobResponse>('/api/commits', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Get job status for async commit processing
 *
 * @param jobId UUID of the job
 * @returns Job status with progress and result
 */
export async function getJob(jobId: string): Promise<JobResponse> {
  return apiRequest<JobResponse>(`/api/jobs/${jobId}`)
}

/**
 * List all commits for a sequence
 *
 * @param sequenceId UUID of the sequence
 * @param options Pagination options
 * @returns List of commits with total count
 */
export async function listCommits(
  sequenceId: string,
  options?: { limit?: number; offset?: number }
): Promise<CommitListResponse> {
  const params = new URLSearchParams()
  if (options?.limit) params.append('limit', options.limit.toString())
  if (options?.offset) params.append('offset', options.offset.toString())

  const queryString = params.toString()
  const endpoint = `/api/commits/${sequenceId}${queryString ? `?${queryString}` : ''}`

  return apiRequest<CommitListResponse>(endpoint)
}

/**
 * Get diff between two commits
 *
 * @param commitId The "to" commit (newer)
 * @param compareId The "from" commit (older)
 * @returns Frame-accurate diff showing all changes
 */
export async function getCommitDiff(
  commitId: string,
  compareId: string
): Promise<CommitDiffResponse> {
  return apiRequest<CommitDiffResponse>(`/api/commits/${commitId}/diff/${compareId}`)
}

/**
 * Fetch OTIO timeline content for a commit from Storage
 *
 * Downloads the OTIO file on-demand for timeline visualization.
 * The backend downloads from Supabase Storage and returns the JSON.
 *
 * @param commitId UUID of the commit
 * @returns Promise that resolves with OTIO JSON string
 */
export async function getCommitOtio(commitId: string): Promise<string> {
  // Get auth token
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token

  if (!token) {
    throw new Error('No authentication token available')
  }

  // Fetch OTIO as raw text (backend returns application/json)
  const response = await fetch(`${BACKEND_URL}/api/commits/${commitId}/otio`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
    throw new Error(errorData.detail || `Failed to fetch OTIO: ${response.statusText}`)
  }

  // Return raw text (which is the OTIO JSON string)
  return await response.text()
}

/**
 * Delete a timeline commit
 *
 * @param commitId UUID of the commit to delete
 * @returns Promise that resolves when delete is complete
 */
export async function deleteCommit(commitId: string): Promise<void> {
  return apiRequest<void>(`/api/commits/${commitId}`, {
    method: 'DELETE',
  })
}

/**
 * Check if the backend is reachable
 *
 * @returns True if backend is healthy
 */
export async function checkBackendHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${BACKEND_URL}/health`)
    return response.ok
  } catch {
    return false
  }
}

// =============================================
// TIMELINE COMPARISON API METHODS (AIO-46)
// =============================================

/**
 * Create an on-demand comparison between two commits
 *
 * @param commitId The "to" commit (newer/target)
 * @param request Comparison configuration
 * @returns Stored comparison with all detected changes
 */
export async function createComparison(
  commitId: string,
  request: ComparisonCreateRequest
): Promise<ComparisonResponse> {
  return apiRequest<ComparisonResponse>(`/api/commits/${commitId}/compare`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

/**
 * Get a stored comparison by ID
 *
 * @param comparisonId UUID of the comparison
 * @returns Comparison with all changes
 */
export async function getComparison(
  comparisonId: string
): Promise<ComparisonResponse> {
  return apiRequest<ComparisonResponse>(`/api/comparisons/${comparisonId}`)
}

/**
 * List all comparisons for a project
 *
 * @param projectId UUID of the project
 * @param options Pagination options
 * @returns List of comparisons with total count
 */
export async function listComparisons(
  projectId: string,
  options?: { limit?: number; offset?: number }
): Promise<ComparisonListResponse> {
  const params = new URLSearchParams()
  if (options?.limit) params.append('limit', options.limit.toString())
  if (options?.offset) params.append('offset', options.offset.toString())

  const queryString = params.toString()
  const endpoint = `/api/projects/${projectId}/comparisons${queryString ? `?${queryString}` : ''}`

  return apiRequest<ComparisonListResponse>(endpoint)
}
